import 'katex/dist/katex.min.css';
import React, { useCallback, useRef, useState } from 'react';
import { InlineMath } from 'react-katex';
import './optimization.css';

export interface RangeSliderProps {
  initialBounds: { lower: number; upper: number }; // base bounds from data
  value: { lower: number; upper: number }; // current bounds (controlled)
  onChange: (value: { lower: number; upper: number }) => void;
  initialGuess?: number; // third marker (now movable)
  onInitialGuessChange?: (value: number) => void; // callback for initial guess changes
  unitsLatex?: string; // LaTeX-friendly or raw units
  disabled?: boolean;
}

const clamp = (x: number, min: number, max: number) => Math.min(max, Math.max(min, x));

const escapeLatexText = (s?: string) =>
  (s || '')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_')
    .replace(/\{/g, '\\{')
    .replace(/\}/g, '\\}');

// Helper function to round to 4 significant figures
const roundToSignificantFigures = (num: number, sigFigs: number = 4): number => {
  if (num === 0) return 0;
  const magnitude = Math.floor(Math.log10(Math.abs(num)));
  const factor = Math.pow(10, sigFigs - 1 - magnitude);
  return Math.round(num * factor) / factor;
};

const RangeSlider: React.FC<RangeSliderProps> = ({
  initialBounds,
  value,
  onChange,
  initialGuess,
  onInitialGuessChange,
  unitsLatex,
  disabled = false
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  
  // Initialize range min/max as 20% expanded from initial bounds
  const [rangeMin, setRangeMin] = useState<number>(() => {
    const span = initialBounds.upper - initialBounds.lower;
    return initialBounds.lower - 0.2 * span;
  });
  const [rangeMax, setRangeMax] = useState<number>(() => {
    const span = initialBounds.upper - initialBounds.lower;
    return initialBounds.upper + 0.2 * span;
  });
  
  const [currentGuess, setCurrentGuess] = useState<number | undefined>(initialGuess);
  const [editing, setEditing] = useState<null | 'min' | 'max' | 'guess'>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [dragging, setDragging] = useState<null | 'min' | 'max' | 'guess'>(null);

  // Update current guess when prop changes
  React.useEffect(() => {
    setCurrentGuess(initialGuess);
  }, [initialGuess]);

  // Calculate the actual min and max from current bounds
  const actualMin = Math.min(value.lower, value.upper);
  const actualMax = Math.max(value.lower, value.upper);

  // Constrain current guess when bounds change
  React.useEffect(() => {
    if (currentGuess != null) {
      const constrainedGuess = clamp(currentGuess, actualMin, actualMax);
      if (constrainedGuess !== currentGuess) {
        setCurrentGuess(constrainedGuess);
        onInitialGuessChange?.(constrainedGuess);
      }
    }
  }, [actualMin, actualMax, currentGuess, onInitialGuessChange]);

  const percentFor = (v: number) => {
    const span = rangeMax - rangeMin;
    if (span === 0) return 0;
    return ((v - rangeMin) / span) * 100;
  };

  const valFromClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return value.lower;
    const rect = el.getBoundingClientRect();
    const p = clamp((clientX - rect.left) / rect.width, 0, 1);
    return rangeMin + p * (rangeMax - rangeMin);
  };

  const onMouseDown = (handle: 'min' | 'max' | 'guess') => (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    setDragging(handle);
  };

  const startEdit = (field: 'min' | 'max' | 'guess') => {
    if (disabled) return;
    setEditing(field);
    if (field === 'min') {
      setEditValue(value.lower.toString());
    } else if (field === 'max') {
      setEditValue(value.upper.toString());
    } else if (field === 'guess' && (currentGuess ?? initialGuess) != null) {
      setEditValue((currentGuess ?? initialGuess)!.toString());
    }
  };

  const finishEdit = () => {
    if (!editing) return;
    
    const numValue = parseFloat(editValue);
    if (isNaN(numValue)) {
      setEditing(null);
      return;
    }

    // Round to 4 significant figures
    const roundedValue = roundToSignificantFigures(numValue, 4);

    // Update range min/max if the new value is outside current range
    if (roundedValue < rangeMin) {
      setRangeMin(roundedValue);
    }
    if (roundedValue > rangeMax) {
      setRangeMax(roundedValue);
    }

    let newBounds: { lower: number; upper: number };
    
    if (editing === 'min') {
      newBounds = { lower: roundedValue, upper: value.upper };
    } else if (editing === 'max') {
      newBounds = { lower: value.lower, upper: roundedValue };
    } else if (editing === 'guess') {
      // Constrain the initial guess to be within actual min/max bounds
      const constrainedGuess = clamp(roundedValue, actualMin, actualMax);
      setCurrentGuess(constrainedGuess);
      onInitialGuessChange?.(constrainedGuess);
      setEditing(null);
      return;
    } else {
      setEditing(null);
      return;
    }

    // Always pass the bounds in the correct order for display
    // The component will handle the real min/max calculation
    onChange(newBounds);
    
    setEditing(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      finishEdit();
    } else if (e.key === 'Escape') {
      setEditing(null);
    }
  };

  const onMove = useCallback((e: MouseEvent) => {
    if (!dragging || disabled) return;
    const proposed = valFromClientX(e.clientX);
    const roundedProposed = roundToSignificantFigures(proposed, 4);
    
    if (dragging === 'min') {
      onChange({ lower: clamp(roundedProposed, rangeMin, rangeMax), upper: value.upper });
    } else if (dragging === 'max') {
      onChange({ lower: value.lower, upper: clamp(roundedProposed, rangeMin, rangeMax) });
    } else if (dragging === 'guess') {
      // Constrain the initial guess to be within actual min/max bounds
      const constrainedGuess = clamp(roundedProposed, actualMin, actualMax);
      setCurrentGuess(constrainedGuess);
      onInitialGuessChange?.(constrainedGuess);
    }
  }, [dragging, disabled, onChange, onInitialGuessChange, value, rangeMin, rangeMax, actualMin, actualMax]);

  const onUp = useCallback(() => setDragging(null), []);

  React.useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      return () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
    }
  }, [dragging, onMove, onUp]);

  const leftPercent = percentFor(value.lower);
  const rightPercent = percentFor(value.upper);
  const displayGuess = currentGuess ?? initialGuess;
  const guessPercent = displayGuess != null ? percentFor(displayGuess) : null;

  const unitsEscaped = escapeLatexText(unitsLatex);
  
  // Helper function to determine if units should be displayed
  const shouldShowUnits = (units?: string) => {
    return units && units.trim() !== '' && units.trim() !== '-';
  };
  
  // Helper function to format value with or without units
  const formatValueWithUnits = (value: number, units?: string) => {
    const roundedValue = roundToSignificantFigures(value, 4);
    const formattedValue = roundedValue.toFixed(2);
    if (shouldShowUnits(units)) {
      return `${formattedValue}\\,\\mathrm{${unitsEscaped}}`;
    }
    return formattedValue;
  };

  return (
    <div className="w-full">
      <div className="relative w-full h-6 flex items-center">
        {/* Track */}
        <div
          ref={trackRef}
          className="absolute w-full h-2 bg-gray-200 rounded-full cursor-pointer"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          {/* Active range */}
          <div
            className="absolute h-full bg-blue-500 rounded-full"
            style={{ left: `${Math.min(leftPercent, rightPercent)}%`, width: `${Math.abs(rightPercent - leftPercent)}%` }}
          />
          {/* Initial guess marker (now draggable) */}
          {guessPercent != null && guessPercent >= 0 && guessPercent <= 100 && (
            <div
              className={`absolute w-3 h-3 bg-purple-500 border-2 border-white rounded-full shadow cursor-pointer ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-600'
              }`}
              style={{ left: `${guessPercent}%`, top: '50%', transform: 'translate(-50%, -50%)' }}
              title="Initial guess (constrained to bounds)"
              onMouseDown={onMouseDown('guess')}
            />
          )}
        </div>

        {/* Min handle */}
        <div
          className={`absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full cursor-pointer transform -translate-y-1/2 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-600'
          }`}
          style={{ left: `${leftPercent}%`, top: '50%', marginLeft: '-8px' }}
          onMouseDown={onMouseDown('min')}
        />

        {/* Min label above (editable) */}
        <div
          className="absolute -top-6 text-sm text-gray-700 bg-white px-1 rounded shadow-sm cursor-pointer hover:bg-gray-50 slider-label-container"
          style={{ 
            left: `${leftPercent}%`, 
            transform: 'translateX(-50%)'
          }}
          onClick={() => startEdit('min')}
        >
          {editing === 'min' ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={finishEdit}
              onKeyDown={handleKeyDown}
              className="w-16 px-1 text-sm border rounded"
              autoFocus
            />
          ) : (
            <div className="optimization-katex-container">
              <InlineMath math={formatValueWithUnits(value.lower, unitsLatex)} />
            </div>
          )}
        </div>

        {/* Max handle */}
        <div
          className={`absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full cursor-pointer transform -translate-y-1/2 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-600'
          }`}
          style={{ left: `${rightPercent}%`, top: '50%', marginLeft: '-8px' }}
          onMouseDown={onMouseDown('max')}
        />

        {/* Max label above (editable) */}
        <div
          className="absolute -top-6 text-sm text-gray-700 bg-white px-1 rounded shadow-sm cursor-pointer hover:bg-gray-50 slider-label-container"
          style={{ 
            left: `${rightPercent}%`, 
            transform: 'translateX(-50%)'
          }}
          onClick={() => startEdit('max')}
        >
          {editing === 'max' ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={finishEdit}
              onKeyDown={handleKeyDown}
              className="w-16 px-1 text-sm border rounded"
              autoFocus
            />
          ) : (
            <div className="optimization-katex-container">
              <InlineMath math={formatValueWithUnits(value.upper, unitsLatex)} />
            </div>
          )}
        </div>
      </div>

      {/* Initial guess label (editable, positioned below the slider) */}
      {displayGuess != null && (
        <div className="relative h-6 mt-2">
          <div
            className="absolute text-sm text-purple-700 font-mono bg-white px-1 rounded shadow-sm cursor-pointer hover:bg-purple-50 slider-label-container"
            style={{ 
              left: `${clamp(guessPercent ?? 0, 0, 100)}%`, 
              transform: 'translateX(-50%)'
            }}
            onClick={() => startEdit('guess')}
          >
            {editing === 'guess' ? (
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={finishEdit}
                onKeyDown={handleKeyDown}
                className="w-16 px-1 text-sm border rounded font-mono"
                autoFocus
              />
            ) : (
              <div className="optimization-katex-container">
                <InlineMath math={formatValueWithUnits(displayGuess, unitsLatex)} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeSlider;
